import {
  getFirstAttributeValue,
  getTotalAppliedReductions,
} from '@scayle/storefront-nuxt'
import type { Product, Variant } from '@scayle/storefront-nuxt'
import { getCategoryLevels, getSizeKey } from '../helpers'
import type {
  TrackAddToBasketParams,
  TrackBasketParams,
  TrackingEvent,
  TrackingPayload,
  TrackRemoveFromBasketParams,
  BasketItemWithCoupon,
} from '../types'
import useProductTrackingLocalStorage from '../useProductTrackingLocalStorage'
import { getSizeLabel } from '#layers/base/types/variant'

const useBasketEvents = (
  track: (event: TrackingEvent, payload: TrackingPayload) => any,
) => {
  const { getStoredProductTrackingItem } = useProductTrackingLocalStorage()
  const currentShop = useCurrentShop()
  const { currency: currencyCode } = currentShop.value ?? {}

  return {
    trackAddToBasket: ({
      product,
      variant,
      quantity = 1,
      coupon,
      type,
    }: TrackAddToBasketParams) => {
      const categoryLevels = getCategoryLevels(product.categories)
      const storedProductTracking = getStoredProductTrackingItem(
        String(product.referenceKey),
      )

      const items: EcommerceItem[] = [
        {
          item_id: product.id.toString(),
          item_sku: variant.referenceKey || '',
          item_name:
            getFirstAttributeValue(product.attributes, 'name')?.label || '',
          item_brand:
            getFirstAttributeValue(product.attributes, 'brand')?.label || '',
          item_variant:
            getFirstAttributeValue(product.attributes, 'color')?.label || '',
          item_size: getSizeLabel(variant),
          item_size_key: variant.referenceKey
            ? getSizeKey(variant.referenceKey) || ''
            : '',
          item_category: categoryLevels.item_category || '',
          item_category2: categoryLevels.item_category2 || '',
          item_category3: categoryLevels.item_category3 || '',
          coupon: coupon || '',
          price: toFloat(variant.price.withTax),
          discount: toFloat(
            getTotalAppliedReductions(variant.price).absoluteWithTax,
          ),
          quantity,
          stock: variant.stock.quantity || 0,
          ordertype: 'shop',
          item_list_name: storedProductTracking?.item_list_name || '',
          item_list_type: storedProductTracking?.item_list_type || '',
        },
      ]

      track('add_to_cart', {
        add_to_cart_type: type,
        ecommerce: {
          currency: currencyCode,
          value: toFloat(variant.price.withTax * quantity),
          net_value: toFloat(variant.price.withoutTax * quantity),
          items,
        },
      })
    },

    trackRemoveFromBasket: ({
      product,
      variant,
      quantity,
      coupon,
      type,
    }: TrackRemoveFromBasketParams) => {
      const categoryLevels = getCategoryLevels(product.categories)
      const storedProductTracking = getStoredProductTrackingItem(
        String(product.referenceKey),
      )

      const items: EcommerceItem[] = [
        {
          item_id: product.id.toString(),
          item_sku: variant.referenceKey || '',
          item_name:
            getFirstAttributeValue(product.attributes, 'name')?.label || '',
          item_brand:
            getFirstAttributeValue(product.attributes, 'brand')?.label || '',
          item_variant:
            getFirstAttributeValue(product.attributes, 'color')?.label || '',
          item_size: getSizeLabel(variant),
          item_size_key: variant.referenceKey
            ? getSizeKey(variant.referenceKey) || ''
            : '',
          item_category: categoryLevels.item_category || '',
          item_category2: categoryLevels.item_category2 || '',
          item_category3: categoryLevels.item_category3 || '',
          price: toFloat(variant.price.withTax),
          discount: toFloat(
            getTotalAppliedReductions(variant.price).absoluteWithTax,
          ),
          coupon: coupon || '',
          quantity,
          stock: variant.stock.quantity || 0,
          ordertype: 'shop',
          item_list_name: storedProductTracking?.item_list_name || '',
          item_list_type: storedProductTracking?.item_list_type || '',
        },
      ]

      track('remove_from_cart', {
        remove_from_cart_type: type,
        ecommerce: {
          currency: currencyCode,
          value: toFloat(variant.price.withTax * quantity),
          net_value: toFloat(variant.price.withoutTax * quantity),
          items,
        },
      })
    },

    trackBasket: ({ basketItems }: TrackBasketParams) => {
      const items: EcommerceItem[] = basketItems.map(
        (basketItem: BasketItemWithCoupon, index: number) => {
          const { product, variant, price } = basketItem

          const categoryLevels = getCategoryLevels(product.categories)
          const storedProductTracking = getStoredProductTrackingItem(
            String(product.referenceKey),
          )

          return {
            item_id: product.id.toString(),
            item_sku: variant.referenceKey || '',
            item_name:
              getFirstAttributeValue(product.attributes, 'name')?.label || '',
            item_brand:
              getFirstAttributeValue(product.attributes, 'brand')?.label || '',
            item_variant:
              getFirstAttributeValue(product.attributes, 'color')?.label || '',
            item_size: getSizeLabel(variant),
            item_size_key: variant.referenceKey
              ? getSizeKey(variant.referenceKey) || ''
              : '',
            item_category: categoryLevels.item_category || '',
            item_category2: categoryLevels.item_category2 || '',
            item_category3: categoryLevels.item_category3 || '',
            price: toFloat(variant.price.withTax),
            discount: toFloat(
              getTotalAppliedReductions(price.unit).absoluteWithTax,
            ),
            index: index + 1,
            coupon: basketItem.promotionCode || '',
            quantity: basketItem.quantity,
            stock: variant.stock.quantity,
            ordertype: 'shop',
            item_list_name: storedProductTracking?.item_list_name || '',
            item_list_type: storedProductTracking?.item_list_type || '',
          }
        },
      )

      track('view_cart', {
        ecommerce: {
          currency: currencyCode,
          value: toFloat(
            basketItems.reduce((a, b) => a + b.price.total.withTax, 0),
          ),
          net_value: toFloat(
            basketItems.reduce((a, b) => a + b.price.total.withoutTax, 0),
          ),
          items,
        },
      })
    },

    // Choose One Size Dialog tracking events
    trackChooseOneSizeDialogSeen: () => {
      track('choose_one_size_dialog_seen', {
        dialog_action: 'modal_shown',
      })
    },

    trackChooseOneSizeDialogKeptAllItems: () => {
      track('choose_one_size_dialog_kept_all_items', {
        dialog_action: 'kept_all_items',
      })
    },

    trackChooseOneSizeDialogReducedItems: () => {
      track('choose_one_size_dialog_reduced_items', {
        dialog_action: 'reduced_items',
      })
    },

    trackChooseOneSizeDialogNotShown: () => {
      track('choose_one_size_dialog_not_shown', {
        dialog_action: 'control_group',
      })
    },

    trackChooseOneSizeDialogDismissed: () => {
      track('choose_one_size_dialog_dismissed', {
        dialog_action: 'dismissed_without_action',
      })
    },

    trackBackInStockRequest: (product: Product, variant: Variant) => {
      const quantity = 1
      const coupon = '' // depends on shopping cart, irrelevant here
      const discount = 0 // depends on shopping cart, irrelevant here

      const categoryLevels = getCategoryLevels(product.categories)
      const storedProductTracking = getStoredProductTrackingItem(
        String(product.referenceKey),
      )

      const item: EcommerceItem = {
        item_id: product.id.toString(),
        item_sku: variant.referenceKey || '',
        item_name:
          getFirstAttributeValue(product.attributes, 'name')?.label || '',
        item_brand:
          getFirstAttributeValue(product.attributes, 'brand')?.label || '',
        item_variant:
          getFirstAttributeValue(product.attributes, 'color')?.label || '',
        item_size: getSizeLabel(variant),
        item_size_key: variant.referenceKey
          ? getSizeKey(variant.referenceKey) || ''
          : '',
        item_category: categoryLevels.item_category || '',
        item_category2: categoryLevels.item_category2 || '',
        item_category3: categoryLevels.item_category3 || '',
        price: toFloat(variant.price.withTax),
        discount,
        coupon,
        quantity,
        stock: variant.stock.quantity,
        ordertype: 'shop',
        item_list_name: storedProductTracking?.item_list_name || '',
        item_list_type: storedProductTracking?.item_list_type || '',
      }

      track('back_in_stock', {
        ecommerce: {
          items: [item],
        },
      })
    },
  }
}
export default useBasketEvents
